import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  ArrowRight,
  QrCode,
  BookOpen,
  Briefcase,
  Mail,
  MessageSquare,
  Globe,
  FileText,
  Sliders,
  BellRing,
  Moon,
  Zap,
} from "lucide-react";

export const NixlevSilentCashier: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Premium Nixlev Native Palette: Warm Ivory & Deep Forest Green
  const bgWarm = "#F8F6F0";
  const bgCard = "#FFFFFF";
  const greenForest = "#1B3B2B";
  const greenEmerald = "#10B981";
  const greenDark = "#059669";
  const goldAccent = "#D4A346";
  const textDark = "#111827";
  const textMuted = "#4B5563";

  // Subtitle Timestamps (Strict Master Safe Zone: y: 1210 - 1370 px)
  const SUBTITLES = [
    { from: 0, to: 180, text: "Pernah gak, lagi tidur jam 2 pagi, tiba-tiba ada notifikasi saldo masuk?", highlight: "notifikasi saldo masuk" },
    { from: 180, to: 300, text: "Bukan sihir, ini mesin otomatis Nixlev.", highlight: "mesin otomatis Nixlev" },
    { from: 300, to: 435, text: "Satu link bio yang rapi, profesional, dan siap jualan.", highlight: "Satu link bio" },
    { from: 435, to: 690, text: "Semua karyamu terpajang: e-book, template Canva, preset foto, hingga jasa.", highlight: "e-book, template, preset, jasa" },
    { from: 690, to: 810, text: "Pembeli tinggal klik, scan QRIS dari m-banking mereka.", highlight: "scan QRIS" },
    { from: 810, to: 975, text: "Detik itu juga, file langsung terkirim otomatis ke WhatsApp & Email.", highlight: "ke WhatsApp & Email" },
    { from: 975, to: 1095, text: "Tanpa kamu perlu bangun. Tanpa drama balas chat satu per satu.", highlight: "Tanpa drama balas chat" },
    { from: 1095, to: 1215, text: "Toko jalan 24 jam, cuan masuk otomatis.", highlight: "Toko jalan 24 jam" },
    { from: 1215, to: 1350, text: "Ubah karyamu jadi cuan pasif di nixlev.com 🚀", highlight: "nixlev.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  // Fluid Motion: Continuous Ambient Float (anti-kaku)
  const ambientFloatY = Math.sin(frame / 22) * 7;

  // Spring Presets
  const appleSpring = (delay: number) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping: 15.35, stiffness: 115.0, mass: 1.0 },
    });

  const snappySpring = (delay: number) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping: 14, stiffness: 180, mass: 0.8 },
    });

  const isOutro = frame >= 1095;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: isOutro ? greenForest : bgWarm,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
        color: isOutro ? "#FFFFFF" : textDark,
        overflow: "hidden",
        transition: "background-color 0.4s ease",
      }}
    >
      {/* Editorial Dot Matrix Texture for Warm Background */}
      {!isOutro && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `radial-gradient(#E2DFD6 1.5px, transparent 1.5px)`,
            backgroundSize: "28px 28px",
            opacity: 0.7,
            pointerEvents: "none",
          }}
        />
      )}

      {/* Subtle Warm Ambient Glow */}
      {!isOutro && (
        <div
          style={{
            position: "absolute",
            top: -150,
            left: 0,
            right: 0,
            height: 700,
            background: `radial-gradient(ellipse at 50% 30%, rgba(27, 59, 43, 0.08) 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />
      )}

      {/* Audio Track */}
      <Audio src={staticFile("nixlev_silent_cashier.mp3")} />

      {/* SFX Synchronization */}
      {frame === 0 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.4} />}
      {frame === 60 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}
      {frame === 180 && <Audio src={staticFile("click.mp3")} volume={0.5} />}
      {frame === 300 && <Audio src={staticFile("whoosh.mp3")} volume={0.5} />}
      {frame === 435 && <Audio src={staticFile("click-soft.mp3")} volume={0.5} />}
      {frame === 690 && <Audio src={staticFile("click.mp3")} volume={0.6} />}
      {frame === 810 && <Audio src={staticFile("chime.mp3")} volume={0.85} />}
      {frame === 1095 && <Audio src={staticFile("chime.mp3")} volume={0.9} />}

      {/* SCENE 1: THE 2 AM NOTIFICATION (0 - 180 frames / 0 - 6s) */}
      {frame < 180 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 260, // Safe Zone Top Boundary
            paddingLeft: 60,
            paddingRight: 60,
          }}
        >
          {/* Time Badge */}
          <div
            style={{
              padding: "10px 24px",
              backgroundColor: "#EFF6FF",
              border: "1px solid #BFDBFE",
              borderRadius: 30,
              fontSize: 22,
              fontWeight: 800,
              color: "#1E40AF",
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 35,
              boxShadow: "0 4px 15px rgba(37, 99, 235, 0.08)",
            }}
          >
            <Moon size={24} color="#2563EB" />
            02:14 AM — Saat Kamu Tidur Nyenyak
          </div>

          <h1
            style={{
              fontSize: 58,
              fontWeight: 900,
              textAlign: "center",
              lineHeight: 1.15,
              marginBottom: 50,
              letterSpacing: "-0.03em",
              color: textDark,
            }}
          >
            Lagi Tidur Jam 2 Pagi, <br />
            <span style={{ color: greenDark }}>Tiba-Tiba Saldo Masuk?</span>
          </h1>

          {/* Floating High-End Bank Notification Card */}
          <div
            style={{
              width: "100%",
              maxWidth: 780,
              backgroundColor: bgCard,
              border: "2px solid #10B981",
              borderRadius: 28,
              padding: "26px 32px",
              display: "flex",
              alignItems: "center",
              gap: 24,
              boxShadow: "0 25px 60px rgba(27, 59, 43, 0.12), 0 8px 20px rgba(0,0,0,0.04)",
              transform: `translateY(${ambientFloatY}px) scale(${interpolate(
                snappySpring(50),
                [0, 1],
                [0.85, 1]
              )})`,
              opacity: interpolate(snappySpring(50), [0, 1], [0, 1]),
            }}
          >
            <div
              style={{
                width: 70,
                height: 70,
                borderRadius: 22,
                backgroundColor: "#ECFDF5",
                border: "2px solid #A7F3D0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BellRing size={38} color="#059669" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: "#065F46" }}>QRIS Pembayaran Diterima</span>
                <span style={{ fontSize: 16, color: "#6B7280", fontWeight: 600 }}>Baru saja</span>
              </div>
              <div style={{ fontSize: 36, fontWeight: 900, color: "#047857", marginTop: 4 }}>
                +Rp 150.000
              </div>
              <div style={{ fontSize: 16, color: "#4B5563", marginTop: 4 }}>
                Penjualan otomatis via <b>nixlev.com/@kreator</b>
              </div>
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* SCENE 2: NIXLEV REVEAL & 3D DESKTOP SHOWCASE (180 - 435 frames / 6 - 14.5s) */}
      {frame >= 180 && frame < 435 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 260,
            paddingLeft: 60,
            paddingRight: 60,
          }}
        >
          <div
            style={{
              padding: "10px 26px",
              backgroundColor: "#DCFCE7",
              border: "1px solid #86EFAC",
              borderRadius: 30,
              fontSize: 22,
              fontWeight: 800,
              color: "#15803D",
              marginBottom: 20,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Zap size={24} />
            Mesin Otomatis Kreator
          </div>

          <h2 style={{ fontSize: 52, fontWeight: 900, textAlign: "center", lineHeight: 1.15, marginBottom: 35 }}>
            Satu Link Bio Rapi. <br />
            <span style={{ color: greenDark }}>Siap Jualan 24 Jam Nonstop.</span>
          </h2>

          {/* 3D Browser Window with Real Desktop Screenshot */}
          <div
            style={{
              width: "100%",
              maxWidth: 820,
              height: 480,
              backgroundColor: "#FFFFFF",
              borderRadius: 24,
              border: "1px solid #D1D5DB",
              boxShadow: "0 30px 70px rgba(0, 0, 0, 0.12), 0 10px 25px rgba(27, 59, 43, 0.08)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              transform: `perspective(1200px) rotateX(${interpolate(
                frame,
                [180, 435],
                [9, 4]
              )}deg) rotateY(${interpolate(
                frame,
                [180, 435],
                [-5, -1]
              )}deg) translateY(${ambientFloatY}px)`,
            }}
          >
            {/* Clean Light Safari macOS Bar */}
            <div
              style={{
                height: 46,
                backgroundColor: "#F9FAFB",
                borderBottom: "1px solid #E5E7EB",
                padding: "0 18px",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FF5F56" }} />
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FFBD2E" }} />
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#27C93F" }} />
              </div>
              <div
                style={{
                  flex: 1,
                  height: 32,
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 15,
                  fontWeight: 600,
                  color: "#374151",
                  gap: 8,
                }}
              >
                <Globe size={15} color="#10B981" />
                nixlev.com/@kreator
              </div>
            </div>
            <Img
              src={staticFile("nixlev_desktop.png")}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "top center",
              }}
            />
          </div>
        </AbsoluteFill>
      )}

      {/* SCENE 3: MULTI-PRODUCT STAGGERED CASCADE (435 - 690 frames / 14.5 - 23s) */}
      {frame >= 435 && frame < 690 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 260,
            paddingLeft: 60,
            paddingRight: 60,
          }}
        >
          <div
            style={{
              padding: "10px 26px",
              backgroundColor: "#EFF6FF",
              border: "1px solid #BFDBFE",
              borderRadius: 30,
              fontSize: 22,
              fontWeight: 800,
              color: "#2563EB",
              marginBottom: 20,
            }}
          >
            Multi-Product Showcase
          </div>

          <h2 style={{ fontSize: 50, fontWeight: 900, textAlign: "center", lineHeight: 1.15, marginBottom: 35 }}>
            Monetisasi Semua Karyamu
          </h2>

          {/* Staggered Flying Product Cards */}
          <div
            style={{
              width: "100%",
              maxWidth: 820,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              transform: `translateY(${ambientFloatY * 0.7}px)`,
            }}
          >
            {[
              { title: "E-Book Masterclass", tag: "PDF Panduan", price: "Rp 99.000", icon: BookOpen, color: "#2563EB", bg: "#EFF6FF" },
              { title: "Preset Lightroom", tag: "10 Mobile Presets", price: "Rp 49.000", icon: Sliders, color: "#7C3AED", bg: "#F5F3FF" },
              { title: "Template Notion/Canva", tag: "Sistem Produktivitas", price: "Rp 129.000", icon: FileText, color: "#D97706", bg: "#FFFBEB" },
              { title: "Jasa 1-on-1 Mentoring", tag: "Konsultasi 60 Menit", price: "Rp 250.000", icon: Briefcase, color: "#059669", bg: "#ECFDF5" },
            ].map((p, idx) => {
              const IconComp = p.icon;
              const cardSp = snappySpring(435 + idx * 8);

              return (
                <div
                  key={idx}
                  style={{
                    backgroundColor: bgCard,
                    border: "1.5px solid #E5E7EB",
                    borderRadius: 22,
                    padding: "24px 22px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: 220,
                    boxShadow: "0 15px 35px rgba(0,0,0,0.06), 0 4px 12px rgba(27, 59, 43, 0.04)",
                    transform: `translateY(${interpolate(cardSp, [0, 1], [40, 0])}px) scale(${interpolate(cardSp, [0, 1], [0.85, 1])})`,
                    opacity: interpolate(cardSp, [0, 1], [0, 1]),
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 14,
                        backgroundColor: p.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconComp size={28} color={p.color} />
                    </div>
                    <div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: textDark }}>{p.title}</div>
                      <div style={{ fontSize: 14, color: textMuted }}>{p.tag}</div>
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 26,
                      fontWeight: 900,
                      color: greenDark,
                      borderTop: "1px solid #F3F4F6",
                      paddingTop: 14,
                    }}
                  >
                    {p.price}
                  </div>
                </div>
              );
            })}
          </div>
        </AbsoluteFill>
      )}

      {/* SCENE 4: INSTANT QRIS & DUAL DELIVERY PIPELINE (690 - 1095 frames / 23 - 36.5s) */}
      {frame >= 690 && frame < 1095 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 260,
            paddingLeft: 60,
            paddingRight: 60,
          }}
        >
          <div
            style={{
              padding: "10px 26px",
              backgroundColor: "#FEF3C7",
              border: "1px solid #FDE68A",
              borderRadius: 30,
              fontSize: 22,
              fontWeight: 800,
              color: "#B45309",
              marginBottom: 20,
            }}
          >
            QRIS Instan ➔ Auto-Delivery
          </div>

          <h2 style={{ fontSize: 50, fontWeight: 900, textAlign: "center", lineHeight: 1.15, marginBottom: 35 }}>
            Scan Sekali. <br />
            <span style={{ color: greenDark }}>File Terkirim Otomatis.</span>
          </h2>

          {/* Central QRIS Card with Laser Sweep */}
          <div
            style={{
              width: 320,
              backgroundColor: "#111827",
              border: `2px solid ${greenEmerald}`,
              borderRadius: 28,
              padding: "20px 20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              boxShadow: "0 25px 60px rgba(0, 0, 0, 0.25), 0 0 40px rgba(16, 185, 129, 0.25)",
              marginBottom: 25,
              transform: `translateY(${ambientFloatY * 0.5}px)`,
            }}
          >
            <div
              style={{
                width: 170,
                height: 170,
                backgroundColor: "#FFFFFF",
                borderRadius: 16,
                padding: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <QrCode size={150} color="#111827" />
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  height: 4,
                  backgroundColor: greenEmerald,
                  boxShadow: "0 0 16px #10B981",
                  top: `${interpolate(frame % 50, [0, 50], [0, 165])}px`,
                }}
              />
            </div>
            <div style={{ marginTop: 12, fontSize: 18, fontWeight: 800, color: "#34D399" }}>
              ✔ Terverifikasi Otomatis
            </div>
          </div>

          {/* Dual Delivery Channel Cards */}
          <div style={{ width: "100%", maxWidth: 820, display: "flex", gap: 16 }}>
            <div
              style={{
                flex: 1,
                backgroundColor: bgCard,
                border: "2px solid #22C55E",
                borderRadius: 20,
                padding: "20px 22px",
                display: "flex",
                alignItems: "center",
                gap: 16,
                boxShadow: "0 10px 30px rgba(34, 197, 94, 0.12)",
              }}
            >
              <MessageSquare size={34} color="#15803D" />
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#14532D" }}>Kirim ke WhatsApp</div>
                <div style={{ fontSize: 14, color: textMuted }}>Link download langsung terkirim.</div>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                backgroundColor: bgCard,
                border: "2px solid #3B82F6",
                borderRadius: 20,
                padding: "20px 22px",
                display: "flex",
                alignItems: "center",
                gap: 16,
                boxShadow: "0 10px 30px rgba(59, 130, 246, 0.12)",
              }}
            >
              <Mail size={34} color="#2563EB" />
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#1E3A8A" }}>Kirim ke Email</div>
                <div style={{ fontSize: 14, color: textMuted }}>Invoice & lisensi resmi aman.</div>
              </div>
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* SCENE 5: EMPOWERMENT & HERO OUTRO (1095 - 1350 frames / 36.5 - 45s) */}
      {frame >= 1095 && (
        <AbsoluteFill
          style={{
            backgroundColor: greenForest,
            color: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: 260,
            paddingLeft: 60,
            paddingRight: 60,
          }}
        >
          {/* Logo with Emerald Halo Glow */}
          <div
            style={{
              padding: 24,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              border: `3px solid ${goldAccent}`,
              boxShadow: "0 0 50px rgba(16, 185, 129, 0.45), 0 20px 45px rgba(0,0,0,0.4)",
              transform: `scale(${appleSpring(1095)}) translateY(${ambientFloatY * 0.6}px)`,
              marginBottom: 25,
            }}
          >
            <Img
              src={staticFile("nixlev_logo.png")}
              style={{
                width: 160,
                height: 160,
                objectFit: "contain",
              }}
            />
          </div>

          <h1
            style={{
              fontSize: 60,
              fontWeight: 900,
              textAlign: "center",
              lineHeight: 1.15,
              marginBottom: 20,
            }}
          >
            Toko Jalan 24 Jam. <br />
            <span style={{ color: "#34D399" }}>Cuan Masuk Otomatis.</span>
          </h1>

          <p
            style={{
              fontSize: 24,
              color: "#CBD5E1",
              textAlign: "center",
              maxWidth: 740,
              lineHeight: 1.4,
              marginBottom: 35,
            }}
          >
            Nol biaya hosting. Nol drama balas chat. Bikin tokomu gratis dalam 5 menit.
          </p>

          {/* CTA Button */}
          <div
            style={{
              backgroundColor: greenEmerald,
              color: "#FFFFFF",
              padding: "22px 52px",
              borderRadius: 30,
              fontSize: 30,
              fontWeight: 900,
              display: "flex",
              alignItems: "center",
              gap: 16,
              boxShadow: "0 20px 50px rgba(16, 185, 129, 0.4)",
            }}
          >
            <span>Buka Tokomu di nixlev.com</span>
            <ArrowRight size={34} color="#FFFFFF" />
          </div>
        </AbsoluteFill>
      )}

      {/* STRICT MASTER SAFE ZONE SUBTITLES (Top: 1210px / Bottom: 580px, Height: 150px) */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            top: 1210, // Exact Gold Safe Zone Y: 1210 - 1370
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            padding: "0 50px",
            zIndex: 100,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(18, 24, 20, 0.92)",
              backdropFilter: "blur(14px)",
              padding: "16px 36px",
              borderRadius: 24,
              border: "1px solid rgba(255, 255, 255, 0.16)",
              boxShadow: "0 15px 35px rgba(0,0,0,0.35)",
              maxWidth: 820,
              textAlign: "center",
              transform: `scale(${spring({
                frame: frame - currentSub.from,
                fps,
                config: { damping: 14, stiffness: 180 },
              })})`,
            }}
          >
            <span style={{ fontSize: 28, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.35 }}>
              {currentSub.highlight ? (
                <>
                  {currentSub.text.split(currentSub.highlight)[0]}
                  <span style={{ color: isOutro ? "#34D399" : goldAccent }}>{currentSub.highlight}</span>
                  {currentSub.text.split(currentSub.highlight)[1]}
                </>
              ) : (
                currentSub.text
              )}
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
